import React, { FC, useEffect } from 'react';
import { Body, Box, useDirection } from '@edreamsodigeo/prisma-design-system';
import Ribbon from '@components/Shared/Ribbon/index';
import { useTracking, useTranslation } from '@edreamsodigeo/frontend-core';
import { useTransportTypeProduct } from '@components/Filters/Components/TransportTypeFilter/Provider';
import { useEqualProminencePrimeDisplayMobileInResults } from 'clientState/hooks/useEqualProminencePrimeDisplayMobileInResults';

export type TicketsLeftRibbonProps = {
  ticketsNumber: number;
  withUKStyle?: boolean;
};

const TICKETS_LIMIT = {
  MIN: 0,
  MAX: 10,
};

export const shallDisplayTicketsLeft = (ticketsNumber: number): boolean => {
  return ticketsNumber > TICKETS_LIMIT.MIN && ticketsNumber < TICKETS_LIMIT.MAX;
};

const TicketsLeftRibbon: FC<TicketsLeftRibbonProps> = ({
  ticketsNumber = 0,
  withUKStyle = false,
}) => {
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const isRtl = useDirection();
  const { product } = useTransportTypeProduct();
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();

  const shouldDisplay =
    shallDisplayTicketsLeft(ticketsNumber) && product !== 'TRAIN';

  useEffect(() => {
    if (shouldDisplay) {
      trackEvent({
        action: 'seats_left',
        label: `seats_left:${ticketsNumber}`,
      });
    }
  }, [shouldDisplay, trackEvent, ticketsNumber]);

  let sideStyle, topStyle;
  if (isEqualProminencePrimeDisplayMobileInResults) {
    topStyle = '-2px';
    sideStyle = '-8px';
  } else {
    topStyle = withUKStyle ? '16px' : '0px';
    sideStyle = withUKStyle ? '-6px' : '-22px';
  }

  const sx = isRtl
    ? { top: topStyle, left: sideStyle }
    : { top: topStyle, right: sideStyle };

  return shouldDisplay ? (
    <Box position="absolute" sx={sx}>
      <Ribbon
        ribbonOrientation={isRtl ? 'right' : 'left'}
        ribbonPlacement="card"
        ribbonColor={{ background: 'red.50', fold: 'red.30' }}
        ribbonWithGradient={false}
      >
        <Box py="tiny" pl="tiny">
          <Body color="neutral.0" type="small" weight="normal">
            {t('results.tickets.left', { count: ticketsNumber })}
          </Body>
        </Box>
      </Ribbon>
    </Box>
  ) : null;
};

export default TicketsLeftRibbon;
